import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import Modal from 'react-bootstrap/lib/Modal';
import { MDText } from 'i18n-react';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Button from '@adsk/bim360-matrix-react-button';

import modalCloseSvg from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import './_ModalConfirmation.scss';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

/**
 * A modal component used for confirmation message. The component consists two buttons: confirm and cancel, and it relies
 * on react-bootstrap component. In order to use it you'll need to make sure you import the 'modals' component from
 * bim360-matrix-bootstrap-sass
 */
export class ModalConfirmation extends React.Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      show: props.show
    };

    this._handleOnHide = this._handleOnHide.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.show !== nextProps.show && this.state.show !== nextProps.show) {
      this.setState({ show: nextProps.show });
    }
  }

  /**
   * hides the modal - after the modal is hidden, onExited will be ran
   */
  _handleOnHide() {
    this.setState({ show: false });
  }

  _handleOnConfirm() {
    this.props.onConfirm();
    if (this.props.hideOnConfirm === true) {
      (this.props.onHide || noop)();
    }
  }

  _renderCloseButton() {
    if (this.props.disableModalExit) return null;

    return (
      <IconButton
        data-testid="ModalConfirmation__close-icon-button"
        onClick={this.props.onHide || this._handleOnHide}
        disabled={this.props.isOnConfirmInProgress}
      >
        <SvgIcon width="15px" height="15px">
          {modalCloseSvg}
        </SvgIcon>
      </IconButton>
    );
  }

  _renderCancelButton() {
    if (this.props.noCancelButton) return null;

    // Doing `this.props.onHide || this._handleOnHide` to avoid making breaking changes.
    // If you pass `this.props.onHide` it means that you want to control the component visual state from the outside.
    return (
      <Button
        styleType={Button.StyleType.CANCEL}
        data-testid="ModalConfirmation__cancel-button"
        className="ModalConfirmation__footer-button"
        onClick={this.props.onHide || this._handleOnHide}
        disabled={this.props.isOnConfirmInProgress}
      >
        {this.props.cancelButtonLabel || this.props[TSymbolProp].translate('CANCEL')}
      </Button>
    );
  }

  /**
   * render function
   */
  render() {
    const disableModalExit = this.props.isOnConfirmInProgress || this.props.disableModalExit;
    const modalBackdrop = disableModalExit ? 'static' : this.props.backdrop;
    return (
      <ThemeConsumer>
        {({ theme }) => (
          <Modal
            {...this.props.modalProps}
            onExited={this.props.onExited}
            bsClass={classNames(
              `ModalConfirmation--${theme}`,
              {
                'ModalConfirmation--is-active': !!this.props.isOnConfirmInProgress
              },
              'ModalConfirmation' // this class has to be last for some reason
            )}
            show={this.state.show}
            onHide={this.props.onHide || this._handleOnHide}
            container={this.props.container}
            backdrop={modalBackdrop}
            keyboard={!disableModalExit}
          >
            <Modal.Header>
              <div className="ModalConfirmation__header-container">
                <div className="ModalConfirmation__header-container-title">{this.props.title}</div>
                {this._renderCloseButton()}
              </div>
            </Modal.Header>
            <Modal.Body>{this.props.message}</Modal.Body>
            <Modal.Footer>
              {this._renderCancelButton()}
              <Button
                styleType={this.props.confirmationButtonStyleType}
                data-testid="ModalConfirmation__confirm-button"
                className={`ModalConfirmation__footer-button ${this.props.confirmationButtonClassName}`}
                onClick={() => this._handleOnConfirm()}
                disabled={
                  this.props.isConfirmationButtonDisabled || this.props.isOnConfirmInProgress
                }
              >
                {this.props.isOnConfirmInProgress
                  ? this.props.confirmationButtonLabelInProgress
                  : this.props.confirmationButtonLabel}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </ThemeConsumer>
    );
  }
}

ModalConfirmation.propTypes = {
  /**
   * Allows to pass props directly to the Modal component
   */
  modalProps: PropTypes.object,
  /**
   * A property (along with `onHide`) making this component an uncontrolled one.
   * When this prop isn't passed the component is controlled.
   * Controls whether or not to show the modal.
   */
  show: PropTypes.bool,
  /**
   * A property (along with `show`) making this component an uncontrolled one.
   * When this prop isn't passed the component is controlled.
   * Callback to when hiding the modal.
   */
  onHide: PropTypes.func,
  /**
   * A callback function for the modal exit event
   */
  onExited: PropTypes.func,
  /**
   * A callback function for the modal confirm event (when clicking on the confirmation button)
   */
  onConfirm: PropTypes.func,
  /**
   * Text for the modal title
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Text or a node to be displayed in the body of the modal
   */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Whether or not the confirmation has progress or not.
   */
  hideOnConfirm: PropTypes.bool,
  /**
   * Whether modal buttons should be disabled
   */
  isOnConfirmInProgress: PropTypes.bool,
  /**
   * Whether confirmation button should be disabled
   */
  isConfirmationButtonDisabled: PropTypes.bool,
  /**
   * Text for confirmation button
   */
  confirmationButtonLabel: PropTypes.string.isRequired,
  /**
   * Text for confirmation button while onConfirm callback is in progress
   */
  confirmationButtonLabelInProgress: PropTypes.string,
  /**
   * A custom CSS clas for confirmation button
   */
  confirmationButtonClassName: PropTypes.string,
  /**
   * The kind of style for the confirmation button
   */
  confirmationButtonStyleType: PropTypes.oneOf(objectValues(Button.StyleType)),
  /**
   * Text for cancel button (if not passed then will default to 'Cancel' based on locale)
   */
  cancelButtonLabel: PropTypes.string,
  /**
   * Whether the cancel button should not be displayed
   */
  noCancelButton: PropTypes.bool,
  /**
   * Determines if the modal cannot be exited (exit button will be hidden and clickoutside will be disabled)
   */
  disableModalExit: PropTypes.bool,
  /**
   * The modal container component. Used for attaching the modal as a child component to the container.
   */
  container: PropTypes.object,
  /**
   * The locale to use for the default cancel button label
   */
  locale: PropTypes.string,
  /**
   * Determines the backdrop of modal.
   */
  backdrop: PropTypes.oneOf(['static', true, false])
};

ModalConfirmation.defaultProps = {
  confirmationButtonStyleType: Button.StyleType.DANGER,
  backdrop: true,
  show: true,
  onConfirm: noop
};

export default withLocale(
  ModalConfirmation,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
